// Relative on purpose: the Bun server (server/publicApi.ts) imports this file too.
import { CITIES, cityIdFromSlug, citySlug } from "./cities";

/**
 * A venue's own URL: `/<city slug>-<venue slug>`, e.g. `/cluj-pomet`
 * (docs/features/venue-detail-offers.md, "Venue pages"). One path segment, so
 * it shares the `/:citySlug` route with the city feed: `parsePlaceParam` tells
 * them apart. The slug is the stored `restaurants.slug` (§35a), unique per
 * city; the prerender, the app and the public API all build the URL here.
 */

/** Same rule as the `restaurants_slug_format` check in §35a. */
export const VENUE_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

/** Longest prefix first, so a city whose slug extends another's still parses. */
const PREFIXES = CITIES.map((c) => ({ id: c.id, prefix: citySlug(c.id) })).sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

/** `/cluj-pomet`, or null when the venue has no slug yet (pre-§35). */
export function venuePath(cityId: string | null | undefined, slug: string | null | undefined): string | null {
  if (!cityId || !slug) return null;
  return `/${citySlug(cityId)}-${slug}`;
}

/** What one path segment names: a city feed, or a city + one of its venues. */
export function parsePlaceParam(
  param: string | null | undefined,
): { cityId: string; venueSlug: string | null } | null {
  if (!param) return null;
  const city = cityIdFromSlug(param);
  if (city) return { cityId: city, venueSlug: null };
  for (const { id, prefix } of PREFIXES) {
    if (!param.startsWith(`${prefix}-`)) continue;
    const rest = param.slice(prefix.length + 1);
    if (VENUE_SLUG_RE.test(rest)) return { cityId: id, venueSlug: rest };
  }
  return null;
}

/** Letters NFD can't split into base + accent (same pairs as the SQL translate map). */
const EXTRA_FOLD: Record<string, string> = { ł: "l", đ: "d", ß: "s" };

/**
 * The default slug for a name. Mirrors `venue_slug_base()` in §35a: the part
 * before " - " or " | ", diacritics folded, kebab-case, at most 48 characters.
 * "POMET - Bucate din Livadă" → "pomet".
 */
export function slugFromName(name: string): string {
  const head = (name.split(" - ")[0] ?? "").split(" | ")[0] ?? "";
  const slug = head
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[łđß]/g, (ch) => EXTRA_FOLD[ch] ?? ch)
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 48)
    .replace(/^-+|-+$/g, "");
  return slug || "local";
}

/**
 * The slug in a venue's URL: the stored one (§35a). Before §35a is pasted the
 * column is absent (`undefined`), and the app, the prerender and the API all
 * fall back to the same name rule, so a build made before the paste still
 * links pages that open. `null` (column present, not filled) = no page yet.
 */
export function venueSlugOf(r: { slug?: string | null; name: string }): string | null {
  return r.slug !== undefined ? r.slug : slugFromName(r.name);
}

/** Why an operator-typed slug can't be used, or null when it can. */
export function venueSlugProblem(cityId: string, slug: string): "format" | "reserved" | null {
  if (!VENUE_SLUG_RE.test(slug)) return "format";
  // `/cluj-napoca` is the city feed (its raw id still resolves), so "napoca" can't be a Cluj venue.
  if (cityIdFromSlug(`${citySlug(cityId)}-${slug}`)) return "reserved";
  return null;
}
